import { NavLink, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import IntlMessages from 'util/IntlMessages';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { pluginAppInIframe } from '../../../util/appUtils';
import BillingTrialMenuItem from '../../../components/kea/BillingTrialMenuItem/BillingTrialMenuItem';
import { useShellAppContext } from '../../../features/shell/context/useShellAppContext';

const OPEN_DELAY_MS = 90;
const CLOSE_DELAY_MS = 140;

const Menu = ({ data }) => {
  const currentApp = useSelector(({ app }) => app.currentApp);
  const { productProfile } = useShellAppContext();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(null);
  const menuRef = useRef(null);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  const clearTimers = useCallback(() => {
    window.clearTimeout(openTimerRef.current);
    window.clearTimeout(closeTimerRef.current);
    openTimerRef.current = null;
    closeTimerRef.current = null;
  }, []);

  const closeMenu = useCallback(() => {
    clearTimers();
    setOpenMenu(null);
  }, [clearTimers]);

  const scheduleOpen = useCallback((menuName) => {
    clearTimers();
    openTimerRef.current = window.setTimeout(() => {
      setOpenMenu(menuName);
      openTimerRef.current = null;
    }, OPEN_DELAY_MS);
  }, [clearTimers]);

  const scheduleClose = useCallback((delay = CLOSE_DELAY_MS) => {
    clearTimers();
    closeTimerRef.current = window.setTimeout(() => {
      setOpenMenu(null);
      closeTimerRef.current = null;
    }, delay);
  }, [clearTimers]);

  useEffect(() => closeMenu(), [closeMenu, location.pathname]);

  useEffect(() => {
    const closeOnOutsidePointer = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) closeMenu();
    };
    document.addEventListener('pointerdown', closeOnOutsidePointer);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer);
      clearTimers();
    };
  }, [clearTimers, closeMenu]);

  // Cloud horizontal and vertical navigation share the exact same pruned
  // section. Embedded plugin search uses its canonical plugin-admin section.
  // Legacy currentApp='search' data remains only for old standalone products.
  const isEmbedded = pluginAppInIframe();
  const useCloudSearchMenu = currentApp === 'search' && productProfile?.id === 'cloud';
  const usePluginSearchMenu = currentApp === 'search' && isEmbedded;
  const effectiveApp = usePluginSearchMenu
    ? 'pluginAdmin'
    : currentApp || (isEmbedded ? 'pluginAdmin' : null);
  const filterRecords = (records) => records.filter(
    (entry) => entry !== false && !entry.hiddenInHorizontalNav,
  );
  const findChilds = (records) => {
    if (!records) return [];
    const filtered = filterRecords(records);
    if (filtered.length === 0) {
      return [];
    }
    return filtered;
  };
  if (!effectiveApp) {
    return null;
  }
  const hasChilds = (records) => findChilds(records).length > 0;
  const menuId = (name) => `top-nav-${name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
  const renderItem = (item) => {
    if (hasChilds(item.children)) {
      const filtered = findChilds(item.children);
      const hasActiveChilds = filtered.filter((e) => e.link === location.pathname).length > 0;
      const isOpen = openMenu === item.name;
      const toggleMenu = () => {
        clearTimers();
        setOpenMenu((current) => (current === item.name ? null : item.name));
      };
      return (
        <li
          className={`nav-item ${hasActiveChilds ? 'active' : ''} ${isOpen ? 'menu-open' : ''} ${item.disabled ? 'disabled' : ''}`}
          key={item.name}
          onMouseEnter={item.disabled ? undefined : () => scheduleOpen(item.name)}
          onMouseLeave={item.disabled ? undefined : () => scheduleClose()}
          onBlur={item.disabled ? undefined : (event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) scheduleClose(0);
          }}
        >
          <span
            className={`nav-link${item.disabled ? ' disabled-nav-link' : ''}`}
            data-testid={item.testId || undefined}
            aria-disabled={item.disabled ? 'true' : undefined}
            aria-haspopup={item.disabled ? undefined : 'menu'}
            aria-expanded={item.disabled ? undefined : isOpen}
            aria-controls={item.disabled ? undefined : menuId(item.name)}
            role="button"
            tabIndex={item.disabled ? -1 : 0}
            onClick={item.disabled ? undefined : toggleMenu}
            onKeyDown={item.disabled ? undefined : (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                toggleMenu();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                closeMenu();
              }
            }}
          >
            <IntlMessages id={item.name} />
            <svg
              className="nav-dropdown-chevron"
              viewBox="0 0 18 18"
              fill="none"
              aria-hidden="true"
            >
              <path d="M5.25 7.25 9 11l3.75-3.75" />
            </svg>
          </span>
          {!item.disabled && (
            <ul
              className="sub-menu"
              id={menuId(item.name)}
              role="menu"
              aria-hidden={!isOpen}
            >
              {filtered.map((child) => (
                <li key={child.name}>
                  <NavLink
                    to={child.link}
                    className={location.pathname === child.link ? 'active' : ''}
                    data-testid={child.testId || undefined}
                    role="menuitem"
                    onClick={closeMenu}
                  >
                    <IntlMessages id={child.name} />
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </li>
      );
    }
    return (
      <li
        className={`nav-item ${location.pathname === item.link ? 'active' : ''} ${item.disabled ? 'disabled' : ''}`}
        key={item.name}
        onMouseEnter={item.disabled ? undefined : () => scheduleClose(OPEN_DELAY_MS)}
      >
        {item.disabled ? (
          <span
            className="prepend-icon disabled-nav-link"
            data-testid={item.testId || undefined}
            aria-disabled="true"
          >
            <span className="nav-text">
              <IntlMessages id={item.name} />
            </span>
          </span>
        ) : (
          <NavLink
            to={item.link}
            className="prepend-icon"
            data-testid={item.testId || undefined}
            onClick={closeMenu}
          >
            <span className="nav-text">
              <IntlMessages id={item.name} />
            </span>
          </NavLink>
        )}
      </li>
    );
  };
  const menu = useCloudSearchMenu
    ? data.cloudSearch?.children
    : data[effectiveApp]?.children;
  if (!menu) {
    throw new Error(`No menu data found for app ${effectiveApp}`);
  }

  return (
    <div
      className="app-main-menu d-none d-md-block app-top-nav"
      ref={menuRef}
      onKeyDown={(event) => {
        if (event.key === 'Escape') closeMenu();
      }}
    >
      <ul className="navbar-nav navbar-nav-mega">
        {menu.map((item) => (renderItem(item)))}
        <BillingTrialMenuItem />
      </ul>
    </div>
  );
};
export default Menu;
